import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tooltip, useTheme } from "@mui/material";
import { FiClock, FiLayers, FiLogIn, FiPackage, FiPauseCircle, FiPlayCircle, FiRotateCcw, FiSliders, FiUser, FiUsers } from "react-icons/fi";
import { Fact, OrgNameEditor, OwnerFact } from "./OrgInlineEdit";
import OrgHistoryPanel from "./OrgHistoryPanel";
import { ErrorBox, OrgModal, PrimaryButton, SecondaryButton, StatusBadge } from "../organisation/OrgModal";
import { METRIC_ICONS, PlanBadge, Pill, Skeleton, fieldClass, useFieldStyle } from "./subscriptionUi";
import UsageBreakdown from "./UsageBreakdown";
import { formatOrgDate, readSession } from "../../utils/organisationApi";
import {
  apiErrorMessage,
  endOfDay,
  fetchAdminOrgPlan,
  fetchAdminPlans,
  addonGain,
  formatDate,
  formatLimit,
  formatNumber,
  hasCustomLimits,
  invalidateAdminOrgPlan,
  limitError,
  metricForAddon,
  METRICS,
  patchOrgPlan,
  planOf,
  toDateInput,
  toLimit,
} from "../../utils/subscriptionData";

const ADDON_TONES = { active: "green", canceling: "amber", expired: "gray", ended: "gray" };
const ADDON_LABELS = { active: "Active", canceling: "Removed, still counting", expired: "Expired", ended: "Ended" };

const TABS = [
  { id: "overview", label: "Overview", icon: FiLayers },
  { id: "limits", label: "Limits & expiry", icon: FiSliders },
  { id: "addons", label: "Add-ons", icon: FiPackage },
  { id: "history", label: "History", icon: FiClock },
];

const TONES = {
  primary: "border-transparent bg-accent-blue text-white hover:bg-[#1D4690]",
  neutral: "bg-white hover:bg-gray-50",
};

const IconAction = ({ label, icon: Icon, onClick, disabled, tone = "neutral", pulse = false, text }) => {
  const button = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`inline-flex h-8 items-center justify-center gap-1.5 rounded-md border text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black disabled:cursor-not-allowed disabled:opacity-40 ${
        text ? "w-8 sm:w-auto sm:px-3" : "w-8"
      } ${TONES[tone]}`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${pulse ? "animate-pulse" : ""}`} aria-hidden="true" />
      {text && <span className="hidden whitespace-nowrap sm:inline">{text}</span>}
    </button>
  );
  // A button with its own words needs no tooltip; icon-only buttons still explain themselves on hover
  if (text) return button;
  return (
    <Tooltip title={label} arrow>
      <span>{button}</span>
    </Tooltip>
  );
};

const SectionTitle = ({ children, note }) => (
  <div className="mb-2">
    <h3 className="text-sm font-semibold">{children}</h3>
    {note && <p className="text-xs text-accent-gray-light">{note}</p>}
  </div>
);

// Everything about one organisation in a single fixed-size dialog: who they are, their plan, limits,
// expiry, add-ons and history. Opened from a row in All organisations.
// The API keeps a plan history only; it records no per-edit reason or editor, so none is shown.
const OrgDetailsModal = ({ organisation, onClose, onSaved, onOpen, opening, onEdited, onToggleSuspend }) => {
  const theme = useTheme();
  const inputStyle = useFieldStyle();
  const orgId = organisation?.id;
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [endDate, setEndDate] = useState("");
  const [limits, setLimits] = useState({});
  const [tab, setTab] = useState("overview");
  const tabRefs = useRef({});

  const applyData = (body) => {
    setData(body);
    setEndDate(toDateInput(planOf(body.plan)?.end_at));
    const base = body.plan?.active ? body.plan.base_limits : null;
    setLimits(Object.fromEntries(METRICS.map(({ key }) => [key, base?.[key] == null ? "" : String(base[key])])));
  };

  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    setData(null);
    setLoadError("");
    setError("");
    setTab("overview");
    invalidateAdminOrgPlan(orgId);
    Promise.all([fetchAdminOrgPlan(orgId), fetchAdminPlans()])
      .then(([body, plans]) => {
        if (cancelled) return;
        applyData(body);
        setCatalog(plans);
      })
      .catch((err) => !cancelled && setLoadError(apiErrorMessage(err, "Could not load this organisation's plan.")));
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const plan = data?.plan;
  const row = planOf(plan);
  const active = Boolean(plan?.active);
  const catalogPlan = catalog.find((item) => item.plan_id === row?.plan_id);
  const addons = data?.addons || [];
  const liveAddons = addons.filter((addon) => addon.status === "active" || addon.status === "canceling");
  const editableExpiry = active && (plan.source === "trial" || plan.source === "manual");
  const deleted = organisation?.status === "deleted";
  const suspended = organisation?.status === "suspended";

  const run = async (name, action, message) => {
    setBusy(name);
    setError("");
    try {
      const body = await action();
      applyData(body);
      onSaved(message);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save the change. Try again."));
    } finally {
      setBusy("");
    }
  };

  const limitProblems = useMemo(
    () => Object.fromEntries(METRICS.map(({ key }) => [key, limitError(limits[key], data?.usage?.[key]?.used || 0)])),
    [limits, data]
  );
  const changedLimits = METRICS.filter(({ key }) => toLimit(limits[key]) !== (plan?.base_limits?.[key] ?? null));
  const limitsInvalid = METRICS.some(({ key }) => limitProblems[key]);

  const resetLimits = () =>
    setLimits(Object.fromEntries(METRICS.map(({ key }) => [key, plan?.base_limits?.[key] == null ? "" : String(plan.base_limits[key])])));
  const fillDefaults = () =>
    setLimits(Object.fromEntries(METRICS.map(({ key }) => [key, catalogPlan?.limits?.[key] == null ? "" : String(catalogPlan.limits[key])])));

  const saveLimits = () =>
    run(
      "limits",
      () => patchOrgPlan(orgId, { limits: Object.fromEntries(changedLimits.map(({ key }) => [key, toLimit(limits[key])])) }),
      `Limits updated for ${organisation.name}. Other organisations are unaffected.`
    );

  const saveExpiry = (value = endDate) =>
    run("expiry", () => patchOrgPlan(orgId, { end_at: endOfDay(value) }), `Expiry for ${organisation.name} is now ${formatDate(endOfDay(value))}.`);

  const onTabKeys = (event) => {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = TABS[(TABS.findIndex((item) => item.id === tab) + step + TABS.length) % TABS.length];
    setTab(next.id);
    tabRefs.current[next.id]?.focus();
  };

  const initial = (organisation?.name || "?").trim().charAt(0).toUpperCase();

  return (
    <OrgModal
      isOpen={Boolean(organisation)}
      onClose={busy ? () => {} : onClose}
      titleId="org-details-title"
      title={
        organisation && (
          <span className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-blue text-base font-bold text-white" aria-hidden="true">
              {initial}
            </span>
            <span className="min-w-0">
              <OrgNameEditor
                organisation={organisation}
                disabled={deleted}
                onSaved={onSaved}
                onUpdated={onEdited}
                badges={
                  <>
                    <StatusBadge status={organisation.status} />
                    {data ? (
                      <>
                        <PlanBadge plan={plan} />
                        {hasCustomLimits(plan, catalogPlan) && <Pill tone="violet">Custom limits</Pill>}
                      </>
                    ) : (
                      <Skeleton className="h-5 w-16" />
                    )}
                  </>
                }
              />
              <span className="block truncate text-xs font-normal text-accent-gray-light">
                {organisation.domain || "No domain"}
              </span>
            </span>
          </span>
        )
      }
      headerActions={
        organisation && (
          <>
            {/* Already inside this organisation: there is nowhere to enter */}
            {readSession().organisationId !== organisation.id && (
              <IconAction label={opening ? "Opening..." : "Enter organisation"} text={opening ? "Opening..." : "Enter organisation"} icon={FiLogIn} tone="primary" pulse={opening} onClick={() => onOpen(organisation)} disabled={opening || deleted} />
            )}
            <IconAction label={suspended ? "Reactivate organisation" : "Suspend organisation"} text={suspended ? "Reactivate" : "Suspend"} icon={suspended ? FiPlayCircle : FiPauseCircle} onClick={() => onToggleSuspend(organisation)} disabled={deleted} />
          </>
        )
      }
      maxWidth="max-w-4xl"
      fixedHeight
      compact
    >
      {organisation && (
        <>
          {/* Who they are: always the same four facts, so nothing moves while the plan loads */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <OwnerFact
              icon={FiUser}
              organisation={organisation}
              created={organisation.createdAt ? `Created ${formatOrgDate(organisation.createdAt)}` : ""}
              disabled={deleted}
              onSaved={onSaved}
              onUpdated={onEdited}
            />
            <Fact icon={FiUsers} label="Members">{organisation.memberCount ?? "—"}</Fact>
          </div>

          {/* Tabs: a segmented control (grey track, raised white selected tab), fixed above the scrolling content */}
          <div
            role="tablist"
            aria-label="Organisation details"
            onKeyDown={onTabKeys}
            className="grid shrink-0 grid-cols-2 gap-1 rounded-xl bg-[#dbe7fb] p-1 sm:grid-cols-4"
          >
            {TABS.map((item) => {
              const selected = tab === item.id;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  ref={(el) => {
                    tabRefs.current[item.id] = el;
                  }}
                  id={`org-tab-${item.id}`}
                  role="tab"
                  type="button"
                  aria-selected={selected}
                  aria-controls={`org-panel-${item.id}`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setTab(item.id)}
                  className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-black ${
                    selected
                      ? "bg-accent-blue font-semibold text-white shadow-[0_2px_6px_rgba(34,82,165,0.35)]"
                      : "font-medium text-[#2a4a86] hover:bg-white/70 hover:text-accent-blue"
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {item.label}
                  {item.id === "addons" && liveAddons.length > 0 && (
                    <span className={`rounded-full px-1.5 text-[11px] leading-4 ${selected ? "bg-white text-accent-blue" : "bg-accent-blue text-white"}`}>{liveAddons.length}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div
            role="tabpanel"
            id={`org-panel-${tab}`}
            aria-labelledby={`org-tab-${tab}`}
            tabIndex={0}
            className="min-h-0 flex-1 overflow-y-auto pr-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-black"
            style={{ scrollbarGutter: "stable" }}
          >
            {loadError ? (
              <ErrorBox>{loadError}</ErrorBox>
            ) : !data ? (
              <div role="status" aria-label="Loading details" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {[0, 1, 2, 3].map((item) => (
                  <Skeleton key={item} className="h-24 w-full" />
                ))}
              </div>
            ) : (
              <>
                {tab === "overview" &&
                  (data.usage && active ? (
                    <UsageBreakdown plan={plan} usage={data.usage} fill />
                  ) : (
                    <section className="overflow-hidden rounded-xl border bg-white" style={{ borderColor: theme.palette.secondary.v4 }}>
                      <header className="border-b bg-[#f3f6fb] px-3 py-2" style={{ borderColor: theme.palette.secondary.v4 }}>
                        <h3 className="text-sm font-semibold">Usage</h3>
                      </header>
                      <div className="flex min-h-[8.25rem] flex-col items-center justify-center gap-1 px-4 text-center">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f3f6fb] text-accent-blue" aria-hidden="true">
                          <FiLayers className="h-4 w-4" />
                        </span>
                        <p className="text-sm font-medium">No usage to show</p>
                        <p className="max-w-xs text-xs text-accent-gray-light">This organisation has no active plan.</p>
                      </div>
                    </section>
                  ))}

                {tab === "limits" && (
                  <div className="space-y-4">
                    {editableExpiry ? (
                      <div className="rounded-xl border p-3" style={{ borderColor: theme.palette.secondary.v4 }}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <h3 className="text-sm font-semibold">{plan.is_trial ? "Trial expiry" : "Plan end date"}</h3>
                            <p className="text-xs text-accent-gray-light">Extend or shorten the trial.</p>
                          </div>
                        </div>
                        <div className="mt-3 flex items-end gap-2">
                          <div>
                            <label htmlFor="org-end-date" className="mb-1 block text-xs font-medium">
                              New end date
                            </label>
                            <input id="org-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`${fieldClass} !h-10 !py-0`} style={inputStyle} />
                          </div>
                          <PrimaryButton onClick={() => saveExpiry()} disabled={!endDate || endDate === toDateInput(plan.end_at) || Boolean(busy)} sx={{ height: "2.5rem" }}>
                            {busy === "expiry" ? "Saving..." : "Save date"}
                          </PrimaryButton>
                        </div>
                      </div>
                    ) : null}

                    {active ? (
                      <div>
                        <SectionTitle note="Applies to this organisation only. Used credits are never reset and a limit can't go below them. Blank means unlimited.">
                          Limits and credits
                        </SectionTitle>
                        <div className="overflow-hidden rounded-xl border" style={{ borderColor: theme.palette.secondary.v4 }}>
                          <div className="hidden md:grid-cols-[minmax(0,1.5fr)_minmax(0,0.7fr)_minmax(0,1fr)_minmax(0,1.4fr)] gap-3 bg-[#f3f6fb] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-accent-gray-light md:grid" aria-hidden="true">
                            <span>Allowance</span>
                            <span>Plan default</span>
                            <span>Used</span>
                            <span>This organisation</span>
                          </div>
                          <ul>
                            {METRICS.map(({ key, label }) => {
                              const usage = data.usage?.[key];
                              const Icon = METRIC_ICONS[key];
                              const current = plan.base_limits?.[key] ?? null;
                              const edited = toLimit(limits[key]) !== current;
                              const differs = current !== (catalogPlan?.limits?.[key] ?? null);
                              const capped = usage && usage.limit !== null && usage.limit !== undefined;
                              const percent = capped && usage.limit > 0 ? Math.min(100, Math.round((usage.used / usage.limit) * 100)) : 0;
                              return (
                                <li
                                  key={key}
                                  className={`grid grid-cols-2 items-center gap-x-3 gap-y-2 border-t px-3 py-2.5 transition-colors md:grid-cols-[minmax(0,1.5fr)_minmax(0,0.7fr)_minmax(0,1fr)_minmax(0,1.4fr)] ${edited ? "bg-[#f6f9fe]" : "hover:bg-gray-50"}`}
                                  style={{ borderColor: theme.palette.secondary.v4 }}
                                >
                                  <div className="col-span-2 flex min-w-0 items-center gap-2.5 md:col-span-1">
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#f3f6fb] text-accent-blue" aria-hidden="true">
                                      {Icon && <Icon className="h-4 w-4" />}
                                    </span>
                                    <div className="min-w-0">
                                      <p className="truncate text-sm font-medium">{label}</p>
                                      {differs && !edited && <p className="text-[11px] text-violet-800">Differs from the plan</p>}
                                      {edited && <p className="text-[11px] text-accent-blue">Unsaved change</p>}
                                    </div>
                                  </div>

                                  <div className="text-sm">
                                    <span className="mb-0.5 block text-[11px] text-accent-gray-light md:hidden">Plan default</span>
                                    <span className="text-accent-gray-light">{catalogPlan ? formatLimit(catalogPlan.limits?.[key] ?? null) : "—"}</span>
                                  </div>

                                  <div className="text-sm">
                                    <span className="mb-0.5 block text-[11px] text-accent-gray-light md:hidden">Used</span>
                                    <span className="font-medium tabular-nums">{usage ? formatNumber(usage.used) : "—"}</span>
                                    {capped && (
                                      <span
                                        className="mt-1 block h-1.5 w-full max-w-[6rem] overflow-hidden rounded-full"
                                        style={{ backgroundColor: theme.palette.secondary.v2 }}
                                        aria-hidden="true"
                                      >
                                        <span
                                          className="block h-full rounded-full"
                                          style={{ width: `${percent}%`, backgroundColor: percent >= 100 ? "#b40909" : percent >= 80 ? "#c27c00" : theme.palette.info.main }}
                                        />
                                      </span>
                                    )}
                                  </div>

                                  <div className="col-span-2 md:col-span-1">
                                    <div className="relative">
                                      <input
                                        aria-label={`${label} limit`}
                                        type="number"
                                        min={0}
                                        value={limits[key] ?? ""}
                                        placeholder="Unlimited"
                                        onChange={(e) => setLimits({ ...limits, [key]: e.target.value })}
                                        aria-invalid={Boolean(limitProblems[key])}
                                        className={`${fieldClass} !py-1.5 pr-9 ${edited ? "!border-accent-blue bg-white" : ""}`}
                                        style={inputStyle}
                                      />
                                      {edited && (
                                        <button
                                          type="button"
                                          onClick={() => setLimits({ ...limits, [key]: current === null ? "" : String(current) })}
                                          aria-label={`Reset ${label} to ${formatLimit(current)}`}
                                          title="Reset"
                                          className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-accent-gray-light hover:bg-black/[0.06] hover:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-black"
                                        >
                                          <FiRotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                                        </button>
                                      )}
                                    </div>
                                    {limitProblems[key] && <p className="mt-1 text-xs text-accent-red">{limitProblems[key]}</p>}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                          <div className="flex flex-wrap items-center gap-2 border-t bg-[#f3f6fb] px-3 py-2.5" style={{ borderColor: theme.palette.secondary.v4 }}>
                            <PrimaryButton onClick={saveLimits} disabled={changedLimits.length === 0 || limitsInvalid || Boolean(busy)}>
                              {busy === "limits" ? "Saving..." : changedLimits.length ? `Save ${changedLimits.length} ${changedLimits.length === 1 ? "change" : "changes"}` : "Save limits"}
                            </PrimaryButton>
                            <SecondaryButton onClick={resetLimits} disabled={changedLimits.length === 0}>Discard</SecondaryButton>
                            <SecondaryButton onClick={fillDefaults} disabled={!catalogPlan} sx={{ ml: { sm: "auto" } }}>Fill plan defaults</SecondaryButton>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <section className="overflow-hidden rounded-xl border bg-white" style={{ borderColor: theme.palette.secondary.v4 }}>
                        <header className="border-b bg-[#f3f6fb] px-3 py-2" style={{ borderColor: theme.palette.secondary.v4 }}>
                          <h3 className="text-sm font-semibold">Limits and credits</h3>
                        </header>
                        <div className="flex min-h-[8.25rem] flex-col items-center justify-center gap-1 px-4 text-center">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f3f6fb] text-accent-blue" aria-hidden="true">
                            <FiSliders className="h-4 w-4" />
                          </span>
                          <p className="text-sm font-medium">No limits to edit</p>
                          <p className="max-w-xs text-xs text-accent-gray-light">This organisation has no active plan.</p>
                        </div>
                      </section>
                    )}
                    <ErrorBox>{error}</ErrorBox>
                  </div>
                )}

                {tab === "addons" &&
                  (addons.length === 0 ? (
                    <section className="overflow-hidden rounded-xl border bg-white" style={{ borderColor: theme.palette.secondary.v4 }}>
                      <header className="border-b bg-[#f3f6fb] px-3 py-2" style={{ borderColor: theme.palette.secondary.v4 }}>
                        <h3 className="text-sm font-semibold">Add-ons</h3>
                      </header>
                      <div className="flex min-h-[8.25rem] flex-col items-center justify-center gap-1 px-4 text-center">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f3f6fb] text-accent-blue" aria-hidden="true">
                          <FiPackage className="h-4 w-4" />
                        </span>
                        <p className="text-sm font-medium">No add-ons</p>
                        <p className="max-w-xs text-xs text-accent-gray-light">This organisation hasn't bought any add-ons yet.</p>
                      </div>
                    </section>
                  ) : (
                    <>
                      <SectionTitle>Add-ons</SectionTitle>
                      <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {addons.map((addon) => {
                          const metric = metricForAddon(addon.addon_type);
                          const live = addon.status === "active" || addon.status === "canceling";
                          return (
                            <li key={addon.id} className="rounded-lg border bg-white p-3" style={{ borderColor: theme.palette.secondary.v4, opacity: live ? 1 : 0.7 }}>
                              <div className="flex items-start justify-between gap-2">
                                <h4 className="text-sm font-semibold">{addon.name}</h4>
                                <Pill tone={ADDON_TONES[addon.status] || "gray"}>{ADDON_LABELS[addon.status] || addon.status}</Pill>
                              </div>
                              <p className="mt-1 text-sm">{addonGain(addon.units, metric)}</p>
                              <p className="mt-0.5 text-xs text-accent-gray-light">
                                {addon.billing === "recurring" ? "Monthly" : "One time"} · {addon.end_at ? `${live ? "until" : "ended"} ${formatDate(addon.end_at)}` : `since ${formatDate(addon.start_at)}`}
                              </p>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  ))}

                {tab === "history" && <OrgHistoryPanel orgId={orgId} history={data.history} />}
              </>
            )}
          </div>
        </>
      )}
    </OrgModal>
  );
};

export default OrgDetailsModal;
